// =====================================================================================
// INVRT Radar - web console - the upsell client (Story PS6)
//
// The typed client and fail-closed parsers for contract C6's member routes, exactly as
// supabase/functions/api/routes/upsell.ts serves them: GET /me/upsell (the active
// catalogue modules the selected organisation does NOT hold, marketing copy only) and
// POST /me/entitlement-requests (a member asks for a module; 201 created, 409
// entitlement_request_already_open). Parsing is deliberately strict: a response that does
// not match the reviewed contract throws an ApiError the states layer can render, and
// nothing is invented in the browser. The route maps its own database tokens
// module-locally on the server; this client mirrors that by leaving every failure as the
// ApiError the server sent, so the UI branches on the token and nothing else.
//
// The upsell catalogue is MARKETING data, not an authority hint: it caches like ordinary
// page data (a 30 second stale window) and never drives access decisions. The gate stays
// with useEntitlements and the server.
// =====================================================================================

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/SessionProvider";
import { ApiError, type ApiClient } from "@/lib/api";
import { useApiClient } from "@/hooks/useApi";

export interface UpsellModule {
  module_code: string;
  name: string;
  marketing_blurb: string;
}

export interface UpsellResponse {
  org_id: string;
  modules: UpsellModule[];
}

/** The two fields the requested / already-requested states need from a 201 body. */
export interface EntitlementRequestReceipt {
  id: string;
  status: string;
}

function invalidUpsellContract(message: string): ApiError {
  return new ApiError({
    token: "internal_error",
    message,
    httpStatus: 502,
    correlationId: null,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyShortText(value: unknown, maxLength: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= maxLength && value === value.trim();
}

/** One not-subscribed catalogue row. The server always fills marketing_blurb (name fallback). */
export function parseUpsellModule(value: unknown): UpsellModule | null {
  if (!isRecord(value)) return null;
  if (!isNonEmptyShortText(value.module_code, 64)) return null;
  if (!isNonEmptyShortText(value.name, 120)) return null;
  if (!isNonEmptyShortText(value.marketing_blurb, 500)) return null;
  return {
    module_code: value.module_code,
    name: value.name,
    marketing_blurb: value.marketing_blurb,
  };
}

export function parseUpsellResponse(value: unknown): UpsellResponse | null {
  if (!isRecord(value)) return null;
  if (!isNonEmptyShortText(value.org_id, 64)) return null;
  if (!Array.isArray(value.modules)) return null;
  const modules: UpsellModule[] = [];
  for (const entry of value.modules) {
    const module = parseUpsellModule(entry);
    if (module === null) return null;
    modules.push(module);
  }
  return { org_id: value.org_id, modules };
}

const REQUEST_STATUSES = new Set(["open", "accepted", "declined"]);

export function parseEntitlementRequestReceipt(value: unknown): EntitlementRequestReceipt | null {
  if (!isRecord(value)) return null;
  const request = value.request;
  if (!isRecord(request)) return null;
  if (!isNonEmptyShortText(request.id, 200)) return null;
  if (typeof request.status !== "string" || !REQUEST_STATUSES.has(request.status)) return null;
  return { id: request.id, status: request.status };
}

/**
 * Reads GET /me/upsell for the selected organisation. A malformed body fails closed as
 * internal_error; a body that names another organisation is forbidden, mirroring the
 * entitlement read's own org check.
 */
export async function fetchUpsell(api: ApiClient, expectedOrgId: string): Promise<UpsellResponse> {
  const response = await api.get<unknown>("/me/upsell");
  const parsed = parseUpsellResponse(response);
  if (parsed === null) {
    throw invalidUpsellContract("The upsell response did not match the RADAR contract.");
  }
  if (parsed.org_id !== expectedOrgId) {
    throw new ApiError({
      token: "forbidden",
      message: "The upsell response belongs to another organisation.",
      httpStatus: 403,
      correlationId: null,
    });
  }
  return parsed;
}

/** Raises POST /me/entitlement-requests for one module and returns the receipt. */
export async function requestModuleAccess(api: ApiClient, moduleCode: string): Promise<EntitlementRequestReceipt> {
  const response = await api.post<unknown>("/me/entitlement-requests", { module_code: moduleCode });
  const receipt = parseEntitlementRequestReceipt(response);
  if (receipt === null) {
    throw invalidUpsellContract("The entitlement request response did not match the RADAR contract.");
  }
  return receipt;
}

/**
 * The not-subscribed catalogue for the selected organisation. Query keys include the
 * organisation id, so one organisation's catalogue cannot render for the next one.
 */
export function useUpsellModules(): UseQueryResult<UpsellResponse> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["me", "upsell", orgId],
    enabled: orgId !== null,
    queryFn: async () => {
      if (orgId === null) {
        throw new ApiError({
          token: "forbidden",
          message: "Choose an organisation before loading the module catalogue.",
          httpStatus: 403,
          correlationId: null,
        });
      }
      return fetchUpsell(api, orgId);
    },
    staleTime: 30_000,
  });
}
